/**
 * Delay Third-Party Scripts
 * Intercepts and delays third-party script loading until page is interactive
 * Works in conjunction with service worker for optimal performance
 */

(function() {
  'use strict';

  const THIRD_PARTY_DOMAINS = [
    'yotpo.com',
    'cdn-widgetsrepository.yotpo.com',
    'cdn-widget-assets.yotpo.com',
    'staticw2.yotpo.com',
  ];

  // Priority domains that should be delayed but with shorter delay (like Rebuy)
  const PRIORITY_DELAYED_DOMAINS = [
    'cdn.rebuyengine.com',
    'cached.rebuyengine.com',
    'rebuyengine.com',
  ];

  /**
   * Check if URL is a third-party script
   */
  function isThirdPartyScript(url) {
    if (!url) return false;
    try {
      const urlObj = new URL(url, window.location.href);
      // Priority delayed scripts (like Rebuy) are handled separately
      if (PRIORITY_DELAYED_DOMAINS.some(domain => urlObj.hostname.includes(domain))) {
        return false; // Don't delay in this script, service worker handles it
      }
      return THIRD_PARTY_DOMAINS.some(domain => urlObj.hostname.includes(domain));
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Check if URL is a priority delayed script
   */
  function isPriorityDelayedScript(url) {
    if (!url) return false;
    try {
      const urlObj = new URL(url, window.location.href);
      return PRIORITY_DELAYED_DOMAINS.some(domain => urlObj.hostname.includes(domain));
    } catch (e) {
      return false;
    }
  }

  /**
   * Delay script loading
   */
  function delayScript(script) {
    const originalSrc = script.src;
    const originalType = script.type || 'text/javascript';
    
    // Remove src temporarily
    script.removeAttribute('src');
    script.setAttribute('data-delayed-src', originalSrc);
    script.setAttribute('data-delayed-type', originalType);
    
    // Mark as delayed
    script.classList.add('delayed-third-party-script');
  }

  /**
   * Load priority delayed scripts (like Rebuy) - shorter delay, loads on DOMContentLoaded
   */
  function loadPriorityDelayedScripts() {
    const delayedScripts = document.querySelectorAll('script[data-delayed-src]');
    
    delayedScripts.forEach((script) => {
      const src = script.getAttribute('data-delayed-src');
      if (!src || !isPriorityDelayedScript(src)) return;
      
      const type = script.getAttribute('data-delayed-type');
      
      // Create new script element
      const newScript = document.createElement('script');
      newScript.src = src;
      if (type) {
        newScript.type = type;
      }
      newScript.async = true;
      newScript.defer = true;
      newScript.setAttribute('fetchpriority', 'low');
      
      // Copy other attributes
      Array.from(script.attributes).forEach(attr => {
        if (!['src', 'type', 'data-delayed-src', 'data-delayed-type'].includes(attr.name)) {
          newScript.setAttribute(attr.name, attr.value);
        }
      });
      
      // Replace old script with new one
      const parent = script.parentNode;
      if (parent) {
        parent.replaceChild(newScript, script);
      }
    });
  }

  /**
   * Load delayed scripts when page is ready (longer delay for non-priority scripts)
   */
  function loadDelayedScripts() {
    const delayedScripts = document.querySelectorAll('script[data-delayed-src]');
    
    delayedScripts.forEach((script) => {
      const src = script.getAttribute('data-delayed-src');
      if (!src) return;
      
      // Skip priority scripts (already loaded)
      if (isPriorityDelayedScript(src)) return;
      
      const type = script.getAttribute('data-delayed-type');
      
      // Create new script element
      const newScript = document.createElement('script');
      newScript.src = src;
      if (type) {
        newScript.type = type;
      }
      newScript.async = true;
      newScript.defer = true;
      newScript.setAttribute('fetchpriority', 'low');
      
      // Copy other attributes
      Array.from(script.attributes).forEach(attr => {
        if (!['src', 'type', 'data-delayed-src', 'data-delayed-type'].includes(attr.name)) {
          newScript.setAttribute(attr.name, attr.value);
        }
      });
      
      // Replace old script with new one
      const parent = script.parentNode;
      if (parent) {
        parent.replaceChild(newScript, script);
      }
    });
  }

  /**
   * Intercept script tags
   */
  function interceptScripts() {
    // Intercept existing scripts
    document.querySelectorAll('script[src]').forEach((script) => {
      if (isThirdPartyScript(script.src)) {
        delayScript(script);
      }
    });

    // Watch for new scripts added dynamically
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
            const scriptElement = node;
            if (scriptElement.src && isThirdPartyScript(scriptElement.src)) {
              delayScript(scriptElement);
            }
          }
        });
      });
    });

    observer.observe(document.head, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        interceptScripts();
        waitForInteractive();
      });
    } else {
      interceptScripts();
      waitForInteractive();
    }
  }

  /**
   * Wait for page to be interactive before loading delayed scripts
   */
  function waitForInteractive() {
    // Load priority scripts (like Rebuy) on DOMContentLoaded - shorter delay for proper initialization
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        loadPriorityDelayedScripts();
        // Notify service worker that DOM is ready
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'DOM_READY' });
        }
      });
    } else if (document.readyState === 'interactive' || document.readyState === 'complete') {
      // DOM already ready, load priority scripts immediately
      loadPriorityDelayedScripts();
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'DOM_READY' });
      }
    }

    // Use requestIdleCallback if available, otherwise setTimeout for other scripts (longer delay)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        // Wait a bit more to ensure page is fully interactive
        setTimeout(loadDelayedScripts, 500);
      }, { timeout: 2000 });
    } else {
      // Fallback: wait for load event + delay
      if (document.readyState === 'complete') {
        setTimeout(loadDelayedScripts, 1000);
      } else {
        if (window.addEventListener) {
          window.addEventListener('load', () => {
            setTimeout(loadDelayedScripts, 1000);
          });
        }
      }
    }
  }

  // Start
  init();
})();

